import { Alert, Box, Button, Chip, CircularProgress, Container, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { CustomStyles } from "../utils/Styled";

const palette = {
    pageBg: "#edf2fa",
    panelBg: "#f9fbff",
    headerBlue: "#0d3a74",
    headerBlueDark: "#072a57",
    textDark: "#13233a",
    muted: "#5f6b7a",
    tableRowOdd: "#f2f7ff",
    tableRowEven: "#e8f1ff",
    tableBorder: "#c7d8f2",
};

const tableColorSchemes = {
    bill: {
        headerBg: "#0d3a74",
        headerBorder: "#335f96",
        subtotalBg: "#d8e6fb",
        subtotalText: "#0d3a74",
        totalBg: "#0d3a74",
        totalBorder: "#335f96",
        rowOdd: palette.tableRowOdd,
        rowEven: palette.tableRowEven,
        containerBorder: palette.tableBorder,
    },
    quotation: {
        // Match the "Invoice Count" KPI card color family from Customer Summary Dashboard.
        headerBg: "#0b6b77",
        headerBorder: "#2393a1",
        subtotalBg: "#d7eff2",
        subtotalText: "#0b6b77",
        totalBg: "#0b6b77",
        totalBorder: "#2393a1",
        rowOdd: "#eef9fb",
        rowEven: "#e2f3f6",
        containerBorder: "#9bcad1",
    },
};

const tableBodyCellSx = {
    color: "#13345f",
    fontSize: 16,
    py: { xs: 1, sm: 1.25 },
    borderColor: "#d7e5fb",
};

const filterSelectSx = {
    minWidth: { xs: "100%", sm: 150 },
    backgroundColor: palette.panelBg,
    borderRadius: 1,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7f9dc4" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.headerBlue },
    "& .MuiInputLabel-root": { color: palette.muted },
    "& .MuiSelect-select": { color: palette.textDark, fontWeight: 600 },
};

const formatLastSynced = (value) => {
    if (!value) return null;

    const parsed = moment(value);
    if (!parsed.isValid()) return null;

    return parsed.subtract(8, "hours").format("DD/MM/YYYY hh:mm A");
};

const liveSyncChipLabel = (lastSyncedAt) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
        <span>{`Live sync from Million Cloud Database · 15 minute cache @Last synced: ${lastSyncedAt}`}</span>
    </Box>
);

const moneyCompact = (value) => {
    const amount = Number(value);
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
    if (!value) return "-";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = months[parsed.getMonth()];
    const year = parsed.getFullYear();

    return `${day} ${month} ${year}`;
};

const initialDetailsData = {
    customerName: "",
    fromPeriod: "",
    toPeriod: "",
    quotationDetails: [],
    invoiceDetails: [],
};

const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const ProductDetailsTable = ({ title, rows, totalLabel, colorScheme = tableColorSchemes.bill, titleColor = palette.headerBlue }) => {
    const tableRows = useMemo(() => {
        const subtotalByDocNo = rows.reduce((accumulator, item) => {
            const docNo = item.documentNo || "";
            return {
                ...accumulator,
                [docNo]: (accumulator[docNo] || 0) + Number(item.amount || 0),
            };
        }, {});

        const mergedRows = [];
        rows.forEach((item, index) => {
            const docNo = item.documentNo || "";

            mergedRows.push({
                type: "data",
                key: `row-${title}-${docNo}-${index}`,
                item,
            });

            const nextDocNo = rows[index + 1]?.documentNo || "";
            if (docNo !== nextDocNo) {
                mergedRows.push({
                    type: "subtotal",
                    key: `subtotal-${title}-${docNo || index}`,
                    subtotalAmount: subtotalByDocNo[docNo] || 0,
                });
            }
        });

        return mergedRows;
    }, [rows, title]);

    const totalAmount = useMemo(
        () => rows.reduce((sum, item) => sum + Number(item.amount || 0), 0),
        [rows]
    );

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h5" color={titleColor} mb={1.25}>
                {title}
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colorScheme.containerBorder}`, borderRadius: 1, backgroundColor: palette.panelBg }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{
                                backgroundColor: colorScheme.headerBg,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: colorScheme.headerBorder,
                                py: { xs: 1, sm: 1.25 },
                            }}>Date</TableCell>
                            <TableCell sx={{
                                backgroundColor: colorScheme.headerBg,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: colorScheme.headerBorder,
                                py: { xs: 1, sm: 1.25 },
                            }}>Doc No</TableCell>
                            <TableCell sx={{
                                backgroundColor: colorScheme.headerBg,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: colorScheme.headerBorder,
                                py: { xs: 1, sm: 1.25 },
                            }}>Description</TableCell>
                            <TableCell sx={{
                                backgroundColor: colorScheme.headerBg,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: colorScheme.headerBorder,
                                py: { xs: 1, sm: 1.25 },
                            }}>Qty</TableCell>
                            <TableCell sx={{
                                backgroundColor: colorScheme.headerBg,
                                color: "#ffffff",
                                fontSize: 14,
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: colorScheme.headerBorder,
                                py: { xs: 1, sm: 1.25 },
                            }}>Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableRows.length > 0 ? (
                            tableRows.map((row, index) =>
                                row.type === "subtotal" ? (
                                    <TableRow key={row.key} sx={{ backgroundColor: colorScheme.subtotalBg }}>
                                        <TableCell colSpan={4} sx={{ ...tableBodyCellSx }} />
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center", fontWeight: 700, color: colorScheme.subtotalText }}>
                                            {moneyCompact(row.subtotalAmount)}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow key={row.key} sx={{ backgroundColor: index % 2 === 0 ? colorScheme.rowOdd : colorScheme.rowEven }}>
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{formatDate(row.item.documentDate)}</TableCell>
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.item.documentNo || "-"}</TableCell>
                                        <TableCell sx={{ ...tableBodyCellSx, whiteSpace: "normal", wordBreak: "break-word" }}>
                                            <Typography variant="body1" sx={{ fontSize: tableBodyCellSx.fontSize }}>
                                                {String(row.item.productDescription || "").trim() || "-"}
                                            </Typography>
                                            {String(row.item.productDescription2 || "").trim() && (
                                                <Typography variant="body2" sx={{ color: palette.muted, mt: 0.25 }}>
                                                    {String(row.item.productDescription2 || "").trim()}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.item.qty ?? 0}</TableCell>
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{moneyCompact(row.item.amount)}</TableCell>
                                    </TableRow>
                                )
                            )
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ ...tableBodyCellSx, textAlign: "center", fontStyle: "italic", color: palette.muted }}>
                                    No records found.
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow sx={{ backgroundColor: colorScheme.totalBg }}>
                            <TableCell
                                colSpan={4}
                                sx={{
                                    ...tableBodyCellSx,
                                    textAlign: "right",
                                    fontWeight: 700,
                                    color: "#ffffff",
                                    borderColor: colorScheme.totalBorder,
                                }}
                            >
                                {totalLabel}
                            </TableCell>
                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center", fontWeight: 700, color: "#ffffff", borderColor: colorScheme.totalBorder }}>
                                {moneyCompact(totalAmount)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export const CustomerDashboardMonthlyDetailsPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [searchParams] = useSearchParams();

    const orgId = searchParams.get("orgId") || "";
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const initialFromMonth = Number(searchParams.get("from_month")) || currentMonth;
    const initialFromYear = Number(searchParams.get("from_year")) || currentYear;
    const initialToMonth = Number(searchParams.get("to_month")) || initialFromMonth;
    const initialToYear = Number(searchParams.get("to_year")) || initialFromYear;

    const [fromMonth, setFromMonth] = useState(initialFromMonth);
    const [fromYear, setFromYear] = useState(initialFromYear);
    const [toMonth, setToMonth] = useState(initialToMonth);
    const [toYear, setToYear] = useState(initialToYear);
    const [firstTransactionYear, setFirstTransactionYear] = useState(null);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);

    const [detailsData, setDetailsData] = useState(initialDetailsData);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const getValidFirstTransactionYear = (value) => {
        const parsedYear = Number(value);
        return Number.isInteger(parsedYear) && parsedYear > 0 && parsedYear <= currentYear
            ? parsedYear
            : null;
    };

    const yearOptions = useMemo(() => {
        const fallbackStartYear = currentYear - 2;
        const startYear = Number.isInteger(firstTransactionYear)
            ? Math.min(firstTransactionYear, currentYear)
            : fallbackStartYear;

        return Array.from(
            { length: currentYear - startYear + 1 },
            (_, index) => currentYear - index
        );
    }, [currentYear, firstTransactionYear]);

    const fetchDetails = async (params) => {
        if (!orgId) {
            setFetchError("Missing required organization parameter for monthly product details.");
            return;
        }

        setIsLoading(true);
        setFetchError("");

        try {
            const { data } = await axios.get(
                process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${orgId}/monthly-quote-invoice-product-details`,
                { params }
            );

            const validFirstTransactionYear = getValidFirstTransactionYear(data?.data?.firstTransactionYear);
            setLastSyncedAt(formatLastSynced(data?.data?.latestTransactionUpdatedDate));
            const payload = data?.data?.monthlyQuoteInvoiceProductDetails || {};
            setDetailsData({
                customerName: payload.customerName || "",
                fromPeriod: payload.fromPeriod || "",
                toPeriod: payload.toPeriod || "",
                quotationDetails: Array.isArray(payload.quotationDetails) ? payload.quotationDetails : [],
                invoiceDetails: Array.isArray(payload.invoiceDetails) ? payload.invoiceDetails : [],
            });
            setFirstTransactionYear(
                validFirstTransactionYear
            );
        } catch (error) {
            if (error.response?.status === 401) {
                logout(error.response?.data?.message || "Your session has expired.");
                return;
            }

            // For missing customers, keep year filters constrained to the current year.
            setFirstTransactionYear(currentYear);
            setLastSyncedAt(null);
            setFetchError(error.response?.data?.message || "Failed to load monthly product details.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails({
            from_month: fromMonth,
            from_year: fromYear,
            to_month: toMonth,
            to_year: toYear,
        });
        // Initial load should happen once using initial URL-derived state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (yearOptions.length === 0) return;

        if (!yearOptions.includes(fromYear)) {
            setFromYear(yearOptions[0]);
        }

        if (!yearOptions.includes(toYear)) {
            setToYear(yearOptions[0]);
        }
    }, [fromYear, toYear, yearOptions]);

    const handleShow = () => {
        fetchDetails({
            from_month: fromMonth,
            from_year: fromYear,
            to_month: toMonth,
            to_year: toYear,
        });
    };

    const handleClose = () => {
        window.close();
        navigate(-1);
    };

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }}>
                <Paper elevation={0} sx={{ backgroundColor: palette.pageBg, border: "1px solid #d5deea", borderRadius: 1, p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography variant="h5" color={palette.headerBlue} fontWeight="bold">
                                Monthly Quote & Invoice Product Details
                            </Typography>
                            {lastSyncedAt && <Chip size="small" label={liveSyncChipLabel(lastSyncedAt)} />}
                        </Box>
                        <Button variant="outlined" sx={{ color: palette.headerBlue, borderColor: "#7f9dc4" }} onClick={handleClose}>
                            Close
                        </Button>
                    </Stack>

                    {fetchError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {fetchError}
                        </Alert>
                    )}

                    {isLoading && (
                        <Alert
                            severity="info"
                            icon={<CircularProgress size={16} color="inherit" />}
                            sx={{ mb: 1.5, color: palette.headerBlue, backgroundColor: "#eaf2ff", border: "1px solid #c7d8f2" }}
                        >
                            Loading monthly product details...
                        </Alert>
                    )}

                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${palette.headerBlue}, ${palette.headerBlueDark})`,
                            borderRadius: 1.5,
                            px: 3,
                            py: 2,
                            mb: 2.5,
                        }}
                    >
                        <Typography variant="h6" color="#fff" fontWeight={500} lineHeight={1.2}>
                            {detailsData.customerName || "-"}
                        </Typography>
                        <Typography variant="subtitle1" color="#dbe8ff" fontWeight={500} lineHeight={1.2} sx={{ mt: 0.75 }}>
                            Period: {detailsData.fromPeriod || "-"} to {detailsData.toPeriod || "-"}
                        </Typography>
                    </Box>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        justifyContent="flex-end"
                        alignItems={{ xs: "stretch", sm: "center" }}
                        sx={{ mb: 2.25 }}
                    >
                        <Typography variant="subtitle1" sx={{ color: palette.textDark, fontWeight: 700 }}>
                            From
                        </Typography>

                        <FormControl size="small" sx={filterSelectSx}>
                            <InputLabel id="details-from-month-label">Month</InputLabel>
                            <Select
                                labelId="details-from-month-label"
                                id="details-from-month"
                                value={fromMonth}
                                label="Month"
                                disabled={isLoading}
                                onChange={(event) => setFromMonth(Number(event.target.value))}
                            >
                                {monthOptions.map((month) => (
                                    <MenuItem key={`from-${month.value}`} value={month.value}>
                                        {month.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ ...filterSelectSx, minWidth: { xs: "100%", sm: 120 } }}>
                            <InputLabel id="details-from-year-label">Year</InputLabel>
                            <Select
                                labelId="details-from-year-label"
                                id="details-from-year"
                                value={fromYear}
                                label="Year"
                                disabled={isLoading}
                                onChange={(event) => setFromYear(Number(event.target.value))}
                            >
                                {yearOptions.map((year) => (
                                    <MenuItem key={`from-year-${year}`} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ color: palette.textDark, fontWeight: 700 }}>
                            To
                        </Typography>

                        <FormControl size="small" sx={filterSelectSx}>
                            <InputLabel id="details-to-month-label">Month</InputLabel>
                            <Select
                                labelId="details-to-month-label"
                                id="details-to-month"
                                value={toMonth}
                                label="Month"
                                disabled={isLoading}
                                onChange={(event) => setToMonth(Number(event.target.value))}
                            >
                                {monthOptions.map((month) => (
                                    <MenuItem key={`to-${month.value}`} value={month.value}>
                                        {month.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ ...filterSelectSx, minWidth: { xs: "100%", sm: 120 } }}>
                            <InputLabel id="details-to-year-label">Year</InputLabel>
                            <Select
                                labelId="details-to-year-label"
                                id="details-to-year"
                                value={toYear}
                                label="Year"
                                disabled={isLoading}
                                onChange={(event) => setToYear(Number(event.target.value))}
                            >
                                {yearOptions.map((year) => (
                                    <MenuItem key={`to-year-${year}`} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button variant="outlined" onClick={handleShow} sx={{ color: palette.headerBlue, borderColor: "#7f9dc4", minWidth: 90 }} disabled={isLoading}>
                            Show
                        </Button>
                    </Stack>

                    <ProductDetailsTable title="Bill Details" rows={detailsData.invoiceDetails} totalLabel="Total Bill Amount" colorScheme={tableColorSchemes.bill} titleColor={palette.headerBlue} />
                    <ProductDetailsTable title="Quotation Details" rows={detailsData.quotationDetails} totalLabel="Total Quotation Amount" colorScheme={tableColorSchemes.quotation} titleColor="#0b6b77" />
                </Paper>
            </Container>
        </div>
    );
};
